import React, { Fragment, useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import moment from 'moment';
import { BlockButton, ButtonGroup, H1, Span } from 'components/Ui';
import ContentfulClient from 'utils/ContentfulClient';
import { getDomainByHost } from 'utils/domainHelper';
import { translateData } from 'utils/translation';
import { openFile } from 'utils/fileDownload';
import {
  accountIcons,
  itemStatusComplete,
  itemStatusActive,
  itemStatusInactive,
} from 'images';
import DeliveryInstructions from './DeliveryInstructionsDrawer';
import styles from './styles.scss';
import commonStyles from '../styles.scss';
import {
  DELIVERY_STATUS_ON_THE_WAY,
  DELIVERY_STATUS_PREPARED,
  DELIVERY_STATUS_READY,
  DELIVERY_STATUS_SCHEDULED,
  deliveryStatuses,
} from './constants';

const NextDeliveryNote = ({ status, deliveryDate }) => {
  const getDeliveryNotes = ({ activeStatus, date }) => {
    switch (activeStatus) {
      case DELIVERY_STATUS_SCHEDULED:
        return `${translateData(
          'Your delivery is scheduled for',
        )} ${date}. ${translateData(
          'Take a look to make sure it’s accurate',
        )}.`;
      case DELIVERY_STATUS_PREPARED:
        return `${translateData('We’re getting your')} ${date} ${translateData(
          'order ready, but you can still make changes',
        )}.`;
      case DELIVERY_STATUS_READY:
        return translateData(
          'The truck is being loaded with your order and getting ready to go!',
        );
      case DELIVERY_STATUS_ON_THE_WAY:
        return translateData(
          'We’re en route to deliver your order. See you soon!',
        );
      default:
        return '';
    }
  };

  const renderDeliveryBLock = (type, icon, label, activeStatus) => {
    const activeStatusIndex = deliveryStatuses.indexOf(activeStatus);
    const typeIndex = deliveryStatuses.indexOf(type);
    const src =
      typeIndex < activeStatusIndex
        ? itemStatusComplete
        : typeIndex === activeStatusIndex
        ? itemStatusActive
        : itemStatusInactive;
    const altText =
      activeStatus === type
        ? `${translateData('Delivery Status Currently Active')}: ${label}`
        : '';
    return (
      <div
        className={classnames(
          activeStatus === type && styles.active,
          styles[type],
        )}
      >
        {activeStatus === type && (
          <span className={styles.arrow}>
            <span className={styles.innerArrow}></span>
          </span>
        )}
        <img className={styles.icon} src={icon} alt="" />
        <Span>{label}</Span>
        <img src={src} alt={altText} />
      </div>
    );
  };

  return (
    <div className={classnames(styles.statusContainer)}>
      <div className={styles.note}>
        {getDeliveryNotes({ activeStatus: status, date: deliveryDate })}
      </div>
      <div className={styles.container}>
        {renderDeliveryBLock(
          DELIVERY_STATUS_SCHEDULED,
          accountIcons.InQueue,
          'Order Scheduled',
          status,
        )}
        {renderDeliveryBLock(
          DELIVERY_STATUS_PREPARED,
          accountIcons.Prepared,
          'Being Prepared',
          status,
        )}
        {renderDeliveryBLock(
          DELIVERY_STATUS_READY,
          accountIcons.InProgress,
          'Almost Ready',
          status,
        )}
        {renderDeliveryBLock(
          DELIVERY_STATUS_ON_THE_WAY,
          accountIcons.Deliver,
          'On the Way',
          status,
        )}
      </div>
      <hr />
    </div>
  );
};

NextDeliveryNote.propTypes = {
  status: PropTypes.string.isRequired,
  deliveryDate: PropTypes.string.isRequired,
};

const DeliveryStatus = ({ deliveryDate }) => {
  const [showContact, changeShowContact] = useState(false);
  const handelShowContact = () => changeShowContact(true);
  const handelHideContact = () => changeShowContact(false);

  const getDeliveryStatus = (date) => {
    const nextDeliveryDate = moment(`${date} 07:00`);
    const hoursTillNextDelivery = nextDeliveryDate.diff(moment(), 'hours');

    if (hoursTillNextDelivery < 0) {
      return DELIVERY_STATUS_ON_THE_WAY;
    }
    if (hoursTillNextDelivery >= 0 && hoursTillNextDelivery < 24) {
      return DELIVERY_STATUS_READY;
    }
    if (hoursTillNextDelivery >= 24 && hoursTillNextDelivery <= 48) {
      return DELIVERY_STATUS_PREPARED;
    }

    return DELIVERY_STATUS_SCHEDULED;
  };

  const getWelcomeEBook = async () => {
    const contentfulClient = new ContentfulClient();
    const domain = getDomainByHost(23);

    const { items } = await contentfulClient.getEntries({
      contentType: 'welcomeEBook',
      query: {
        'site.sys.contentType.sys.id': domain.type,
        'site.fields.slug': domain.slug,
      },
    });

    if (items.length !== 0) {
      openFile({ url: items[0].fields.eBook.fields.file.url });
    } else {
      openFile({
        url:
          'https://water.com/files/nonbrand/02072017_Retention_Progm_Welcome_eBook_Default.pdf',
      });
    }
  };

  const formattedDeliveryDate = moment(deliveryDate).format('MMMM D');
  const badgeInfo = {
    dayName: moment(deliveryDate).format('ddd'),
    day: moment(deliveryDate).format('DD'),
    month: moment(deliveryDate).format('MMM'),
  };
  const status = getDeliveryStatus(deliveryDate);

  const showBlock = false;

  return (
    <Fragment>
      <div className={commonStyles.blockContainer}>
        <div className={commonStyles.header}>
          <div className={commonStyles.titleContainer}>
            <div className={commonStyles.badgeContainer}>
              <span>{badgeInfo.dayName}</span>
              <span className={commonStyles.badgeDay}>{badgeInfo.day}</span>
              <span>{badgeInfo.month}</span>
            </div>
            <div>
              <H1 className={commonStyles.title}>Delivery Status</H1>
              <span>
                <Span>Your delivery is set for</Span> {formattedDeliveryDate}.
              </span>
            </div>
          </div>
          <NextDeliveryNote
            status={status}
            deliveryDate={formattedDeliveryDate}
          />
        </div>
        {showBlock && (
          <ButtonGroup fullWidth>
            <BlockButton
              onClick={getWelcomeEBook}
              icon={<img src={accountIcons.Question} alt="question" />}
              title="How to prepare"
              text="Here are some pointers to prepare for your delivery."
            />
            <BlockButton
              icon={<img src={accountIcons.Message} alt="message" />}
              title="Contact your driver"
              text="Got something to let us know? Reach out anytime."
              onClick={handelShowContact}
            />
          </ButtonGroup>
        )}
      </div>
      {showContact && <DeliveryInstructions onClose={handelHideContact} />}
    </Fragment>
  );
};

DeliveryStatus.propTypes = {
  deliveryDate: PropTypes.string.isRequired,
};

export default DeliveryStatus;
